import { Printer, Download } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import DisclosureQuickStatsBar from './DisclosureQuickStatsBar';
import type { EventDisclosureReport } from '../../types/disclosure';

function fmt(ts: string | null | undefined) {
  if (!ts) return '—';
  return formatDateTime(ts);
}

function formTypeLabel(formType: string) {
  if (formType === 'minor_injury') return 'Minor Injury';
  if (formType === 'paracetamol_screening') return 'Paracetamol Screening';
  return 'Patient Report Form';
}

function resourceTypeLabel(type: string | null | undefined) {
  if (!type) return '—';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

interface EventDisclosureReportViewProps {
  report: EventDisclosureReport;
  showActions?: boolean;
  onPrint?: () => void;
  onDownload?: () => void;
}

export default function EventDisclosureReportView({
  report,
  showActions = true,
  onPrint,
  onDownload,
}: EventDisclosureReportViewProps) {
  const { event, resources, incidents, summary } = report;

  return (
    <div className="disclosure-report max-w-4xl mx-auto">
      {showActions && (
        <div className="no-print flex items-center gap-2 mb-6">
          <button
            type="button"
            onClick={onPrint}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
          >
            <Printer className="w-3.5 h-3.5" />
            Print
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-accent"
          >
            <Download className="w-3.5 h-3.5" />
            Download JSON
          </button>
        </div>
      )}

      <header className="border-b border-border pb-4 mb-6">
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
          Event Disclosure Report — {report.detailLevel === 'full' ? 'Full report' : 'Short report'}
        </p>
        <h1 className="text-xl font-bold mt-1">{event.name}</h1>
        <p className="text-xs font-mono text-muted-foreground mt-0.5">{event.id}</p>
        <p className="text-[11px] text-muted-foreground mt-2">
          Generated {fmt(report.generatedAt)}
        </p>
        {report.detailLevel === 'full' && summary.fullOptions && (
          <p className="text-[11px] text-muted-foreground mt-2 border border-border rounded-lg px-3 py-2">
            Full sections included:{' '}
            {[
              summary.fullOptions.incidents && 'incident details & logs',
              summary.fullOptions.resources && 'resource MDT data',
              summary.fullOptions.patientIds.length > 0 &&
                `${summary.fullOptions.patientIds.length} patient record(s)`,
            ]
              .filter(Boolean)
              .join(' · ') || 'none'}
          </p>
        )}
      </header>

      <DisclosureQuickStatsBar report={report} />

      <section className="mb-8">
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Event Details
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Category</dt>
            <dd>{event.category}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Status</dt>
            <dd className="uppercase">{event.status}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event date</dt>
            <dd>{event.eventDate || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Start time</dt>
            <dd>{event.eventStartTime || '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event opened</dt>
            <dd>{fmt(event.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted-foreground">Event closed</dt>
            <dd>{fmt(event.closedAt)}</dd>
          </div>
          {event.closeReason && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Close reason</dt>
              <dd>{event.closeReason}</dd>
            </div>
          )}
          {event.location?.address && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Location</dt>
              <dd>{event.location.address}</dd>
            </div>
          )}
          {event.description && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] uppercase text-muted-foreground">Description</dt>
              <dd className="whitespace-pre-wrap">{event.description}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Resources ({summary.resourceCount})
        </h2>
        {resources.length === 0 ? (
          <p className="text-sm text-muted-foreground">No resources recorded for this event.</p>
        ) : resources.some((r) => r.mdtSessions?.length || r.mdtUsername) ? (
          <div className="space-y-4">
            {resources.map((r) => (
              <div key={r.id} className="border border-border rounded-lg p-3 text-xs">
                <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
                  <span className="font-mono font-semibold">{r.callsign}</span>
                  <span>{resourceTypeLabel(r.type)}</span>
                  {r.department && <span className="text-muted-foreground">{r.department}</span>}
                  {r.status && (
                    <span className="text-[10px] uppercase text-muted-foreground ml-auto">{r.status}</span>
                  )}
                </div>
                {(r.mdtUsername || r.mdtDisplayName) && (
                  <p className="text-muted-foreground mb-2">
                    Current MDT user:{' '}
                    <span className="text-foreground font-mono">{r.mdtUsername || r.mdtDisplayName}</span>
                    {r.mdtDisplayName && r.mdtUsername && r.mdtDisplayName !== r.mdtUsername && (
                      <span> ({r.mdtDisplayName})</span>
                    )}
                  </p>
                )}
                {r.mdtSessions && r.mdtSessions.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-1.5">
                      MDT sign-on history
                    </h4>
                    <div className="space-y-1">
                      {r.mdtSessions.map((s, i) => (
                        <p key={`${s.timestamp}-${i}`} className="text-[11px] border-l-2 border-border pl-2">
                          <span className="font-mono text-muted-foreground">{fmt(s.timestamp)}</span>
                          <span className="mx-1.5 font-semibold">{s.action}</span>
                          {s.username && <span className="font-mono">{s.username}</span>}
                          {s.details && <span className="text-muted-foreground ml-1">— {s.details}</span>}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <table className="w-full text-xs border-collapse disclosure-table">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-3">Callsign</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2">Department</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => (
                <tr key={r.id} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono">{r.callsign}</td>
                  <td className="py-2 pr-3">{resourceTypeLabel(r.type)}</td>
                  <td className="py-2">{r.department || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-muted-foreground">
          Incidents ({summary.incidentCount})
        </h2>
        {incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No incidents recorded for this event.</p>
        ) : (
          <div className="space-y-8">
            {incidents.map((inc) => (
              <article key={inc.id} className="border border-border rounded-lg p-4 break-inside-avoid">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
                  <h3 className="font-mono font-bold text-sm">{inc.id}</h3>
                  <span className="text-sm">{inc.title}</span>
                  <span className="text-[10px] uppercase text-muted-foreground ml-auto">{inc.status}</span>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-4">
                  <div>
                    <dt className="text-[10px] uppercase text-muted-foreground">Opened</dt>
                    <dd>{fmt(inc.reportedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-muted-foreground">Closed</dt>
                    <dd>{fmt(inc.closedAt)}</dd>
                  </div>
                  {inc.closeResult && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Result</dt>
                      <dd>{inc.closeResult}</dd>
                    </div>
                  )}
                  {inc.location?.address && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Location</dt>
                      <dd>{inc.location.address}</dd>
                    </div>
                  )}
                  {inc.description && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Description</dt>
                      <dd className="whitespace-pre-wrap">{inc.description}</dd>
                    </div>
                  )}
                  {inc.tags && inc.tags.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Tags</dt>
                      <dd>{inc.tags.join(', ')}</dd>
                    </div>
                  )}
                  {inc.assignedUnits && inc.assignedUnits.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-[10px] uppercase text-muted-foreground">Assigned units</dt>
                      <dd className="font-mono">{inc.assignedUnits.join(', ')}</dd>
                    </div>
                  )}
                </dl>

                {inc.assignments.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Resource assignments
                    </h4>
                    <table className="w-full text-[11px] border-collapse disclosure-table">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-1.5 pr-2">Callsign</th>
                          <th className="py-1.5 pr-2">Type</th>
                          <th className="py-1.5 pr-2">Assigned</th>
                          <th className="py-1.5 pr-2">En route</th>
                          <th className="py-1.5 pr-2">On scene</th>
                          <th className="py-1.5">Cleared</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inc.assignments.map((a) => (
                          <tr key={a.callsign} className="border-b border-border/50">
                            <td className="py-1.5 pr-2 font-mono">{a.callsign}</td>
                            <td className="py-1.5 pr-2">{resourceTypeLabel(a.resourceType)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.assignedAt)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.enRouteAt)}</td>
                            <td className="py-1.5 pr-2 font-mono">{fmt(a.onSceneAt)}</td>
                            <td className="py-1.5 font-mono">{fmt(a.clearedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {inc.prfs.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Patient records ({inc.prfs.length})
                    </h4>
                    <div className="space-y-3">
                      {inc.prfs.map((prf) => (
                        <div key={prf.id} className="rounded-md bg-muted/40 p-3 text-xs">
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1.5">
                            <span className="font-mono font-semibold">{prf.id}</span>
                            <span className="text-muted-foreground">{formTypeLabel(prf.formType)}</span>
                            {prf.isPaper && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-semibold">
                                Paper
                              </span>
                            )}
                            {prf.referenceOnly && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                                Reference only
                              </span>
                            )}
                            {prf.fullData && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold">
                                Full detail
                              </span>
                            )}
                            <span className="text-muted-foreground ml-auto">{fmt(prf.submittedAt)}</span>
                          </div>

                          {prf.referenceOnly ? (
                            <p className="text-muted-foreground">
                              Reference recorded — clinical detail requires <strong>prfs:view</strong> permission.
                            </p>
                          ) : (
                            <>
                              {prf.patientName && (
                                <p>
                                  <span className="text-muted-foreground">Patient / notes: </span>
                                  {prf.patientName}
                                </p>
                              )}
                              {prf.injuryType && (
                                <p>
                                  <span className="text-muted-foreground">Injury / complaint: </span>
                                  {prf.injuryType}
                                </p>
                              )}
                              {prf.resultingAction && (
                                <p>
                                  <span className="text-muted-foreground">Resulting action: </span>
                                  {prf.resultingAction}
                                </p>
                              )}
                              {prf.dealtWithBy && prf.dealtWithBy.length > 0 && (
                                <p>
                                  <span className="text-muted-foreground">Dealt with by: </span>
                                  {prf.dealtWithBy.join(', ')}
                                </p>
                              )}
                            </>
                          )}

                          {!prf.referenceOnly && prf.fullData && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-[10px] font-semibold text-primary">
                                Full PRF clinical data
                              </summary>
                              <pre className="mt-2 p-2 rounded bg-background border border-border text-[10px] overflow-x-auto whitespace-pre-wrap">
                                {JSON.stringify(prf.fullData, null, 2)}
                              </pre>
                            </details>
                          )}

                          {!prf.referenceOnly && (prf.accessLogs?.length || prf.disclosureLogs?.length) ? (
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                              {prf.accessLogs?.map((a) => (
                                <p key={a.id} className="text-[10px] text-muted-foreground">
                                  Access: {a.username} — {a.reason} ({fmt(a.accessedAt)})
                                </p>
                              ))}
                              {prf.disclosureLogs?.map((d) => (
                                <p key={d.id} className="text-[10px] text-amber-400/90">
                                  Disclosed: {d.username} — {d.reason} ({fmt(d.disclosedAt)})
                                </p>
                              ))}
                            </div>
                          ) : null}

                          <p className="text-muted-foreground mt-1">Submitted by {prf.submittedBy}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {inc.logs && inc.logs.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2">
                      Incident log &amp; remarks ({inc.logs.length})
                    </h4>
                    <div className="space-y-1.5 max-h-96 overflow-y-auto">
                      {inc.logs.map((log) => (
                        <div key={log.id} className="text-[11px] border-l-2 border-border pl-2 py-0.5">
                          <span className="font-mono text-muted-foreground">{fmt(log.timestamp)}</span>
                          <span className="mx-1.5 text-primary font-semibold">{log.action}</span>
                          <span className="text-muted-foreground">{log.userName}</span>
                          <p className="text-foreground mt-0.5 whitespace-pre-wrap">{log.details}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {summary.patientNamesRedacted && (
        <p className="mt-6 text-[11px] text-muted-foreground border border-border rounded-lg px-3 py-2">
          Patient names in this shared report are redacted to initials. Full names are available only in
          Command or when a share link was created with “Include full patient names”.
        </p>
      )}

      {summary.clinicalDataIncluded === false && (
        <p className="mt-4 text-[11px] text-muted-foreground border border-border rounded-lg px-3 py-2">
          Patient records show unique reference numbers only — no clinical detail is included without
          appropriate permissions.
        </p>
      )}

      <footer className="mt-10 pt-4 border-t border-border text-[10px] text-muted-foreground">
        CADSys Command — confidential event disclosure. Contains operational and patient-related data;
        handle in accordance with applicable data protection requirements.
      </footer>
    </div>
  );
}

export function downloadDisclosureJson(report: EventDisclosureReport, filename?: string) {
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `disclosure-${report.event.id}-${report.generatedAt.slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function printDisclosureReport() {
  window.print();
}
